/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-10-31 18:26:11
 **/

import { _T } from "@rfice/language";

const LOGTAG = "#AudioVisualizer#";

type fftSizeType = 32 | 64 | 128 | 256 | 512 | 1024 | 2048 | 4096 | 8192 | 16384 | 32768;

export default class AudioVisualizer {
	audioContext: AudioContext;
	stream: MediaStream;
	source: MediaStreamAudioSourceNode;
	analyser: AnalyserNode;
	gainNode: GainNode;
	dataArray: Uint8Array;
	volumeAverage: number;

	rafId: number | null;

	constructor(stream: MediaStream) {
		this.rafId = null;
		this.audioContext = new AudioContext();
		this.stream = stream;
		this.source = this.audioContext.createMediaStreamSource(this.stream);
		this.analyser = this.audioContext.createAnalyser();
		this.gainNode = this.audioContext.createGain();
		this.gainNode.gain.value = 0.5;
		this.analyser.fftSize = 32;
		this.volumeAverage = 0;

		// Uint8Array 는 0~255 사이로 제한된다. ( 0 ~ 255 범위를 벗어난 값이 입력되면 )
		// Uint8Array - [ 0 ~ 255 ] 제한 값을 가지는 배열을 frequencyBinCount 길이만큼 만든다.
		this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
		// Uint8ClampedArray 와 Uint8Array 의 차이
		// 구속의 차이 Uint8ClampedArray 는 0 이하의 값이 들어가면 0, 255 이상의 값이 들어가면 255

		this.draw = this.draw.bind(this);

		this.init();
	}

	private init() {
		try {
			if (this.stream.getAudioTracks().length === 0) {
				throw Error(_T("미디어 스트림에 audio가 존재하지 않습니다."));
			}

			this.source.connect(this.gainNode);
			this.analyser.minDecibels = -100;
			this.analyser.maxDecibels = 0;
			// this.setFftSize(512);
			this.gainNode.connect(this.analyser);

			this.draw();
		} catch (error) {
			console.warn(LOGTAG, "error : ", error);
		}
	}

	private draw() {
		this.rafId = requestAnimationFrame(this.draw);
		// getByteFrequencyData 는 현재 주파수 데이터를 복사한다.
		// getByteTimeDomainData는 파형 테이터를 복사한다.
		// 주파수 데이터는 0 ~ 255 까지의 정수로 구성.
		// dataArray 배열의 크기보다 적은 요소가 있으면 해당 요소는 삭제, 많은 요소가 있으면 초과 요소는 무시한다.
		this.analyser.getByteFrequencyData(this.dataArray);

		this.volumeAverage =
			(this.dataArray.reduce((acc, volume) => acc + volume) / this.analyser.frequencyBinCount / 255) * 100;
	}

	public setFftSize(number: fftSizeType) {
		this.analyser.fftSize = number;
		this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
	}

	public mute() {
		this.gainNode.gain.value = 0;
		this.stop();
	}

	public unMute() {
		this.gainNode.gain.value = 1;
	}

	public stop() {
		if (!this.rafId) return;
		cancelAnimationFrame(this.rafId);
	}

	public play() {
		if (!this.rafId) return;
		this.draw();
	}

	public disconnect() {
		if (!this.rafId) return;
		this.stop();
	}
}
